<template>
  <div class="nutrition-dashboard">
    <!-- Summary Cards -->
    <div class="summary-cards">
      <div class="summary-card total">
        <div class="card-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
          </svg>
        </div>
        <div class="card-content">
          <span class="card-value">{{ dashboardData.total_players || 0 }}</span>
          <span class="card-label">Total Players</span>
        </div>
      </div>

      <div class="summary-card logged">
        <div class="card-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 11l3 3L22 4"></path>
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
          </svg>
        </div>
        <div class="card-content">
          <span class="card-value">{{ dashboardData.logged_today || 0 }}</span>
          <span class="card-label">Logged Today</span>
        </div>
      </div>

      <div class="summary-card hydration">
        <div class="card-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
          </svg>
        </div>
        <div class="card-content">
          <span class="card-value">{{ dashboardData.avg_hydration || 0 }}%</span>
          <span class="card-label">Avg Hydration</span>
        </div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>Loading dashboard...</span>
    </div>

    <template v-else>
      <!-- Needs Attention Section -->
      <div class="attention-section">
        <div class="section-header">
          <h3>Needs Attention</h3>
          <span class="attention-count" v-if="needsAttention.length > 0">
            {{ needsAttention.length }}
          </span>
        </div>

        <div v-if="needsAttention.length > 0" class="attention-list">
          <div
            v-for="player in needsAttention"
            :key="player.id"
            class="attention-item"
            @click="selectPlayer(player)"
          >
            <div class="player-avatar">
              <img
                v-if="player.image"
                :src="player.image"
                :alt="player.name"
                class="avatar-img"
              />
              <div v-else class="avatar-placeholder">
                {{ getInitials(player.name) }}
              </div>
            </div>
            <div class="player-info">
              <span class="player-name">{{ player.name }}</span>
              <span class="attention-reason">{{ player.reason }}</span>
            </div>
            <div class="status-indicator" :style="getStatusStyle(player)">
              {{ player.status_label || 'Low' }}
            </div>
          </div>
        </div>

        <div v-else class="all-good">
          <div class="all-good-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <p>All players are on track today!</p>
        </div>
      </div>

      <!-- Daily Trends -->
      <div class="trends-section">
        <div class="section-header">
          <h3>This Week's Trends</h3>
        </div>

        <div class="trends-chart">
          <div class="trend-bars">
            <div
              v-for="day in weekTrends"
              :key="day.date"
              class="trend-bar-container"
            >
              <div
                class="trend-bar"
                :style="{ height: day.compliance + '%' }"
                :class="{ today: day.isToday }"
              ></div>
              <span class="trend-label">{{ day.dayName }}</span>
            </div>
          </div>
          <div class="trend-legend">
            <span>Team Compliance %</span>
          </div>
        </div>
      </div>

      <!-- Quick Stats -->
      <div class="quick-stats">
        <div class="stat-card">
          <span class="stat-value">{{ dashboardData.avg_meals_per_day || 0 }}</span>
          <span class="stat-label">Avg Meals/Day</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ dashboardData.top_compliance || 0 }}%</span>
          <span class="stat-label">Best Compliance</span>
        </div>
        <div class="stat-card">
          <span class="stat-value">{{ notLoggedCount }}</span>
          <span class="stat-label">Not Logged Today</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "NutritionDashboard",

  props: {
    date: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
  },

  data() {
    return {
      loading: true,
      dashboardData: {},
      weekTrends: [],
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },

    isCoach() {
      const userType = this.getProfile?.userType;
      const position = this.getProfile?.position;
      return userType === "Coach" || position === "Coach" || position === "HeadCoach";
    },

    clubId() {
      return this.getProfile?.club_id || this.getProfile?.clubId;
    },

    needsAttention() {
      return this.dashboardData.needs_attention || [];
    },

    notLoggedCount() {
      const total = this.dashboardData.total_players || 0;
      const logged = this.dashboardData.logged_today || 0;
      return Math.max(0, total - logged);
    },
  },

  watch: {
    date() {
      this.loadDashboard();
    },
  },

  mounted() {
    this.loadDashboard();
  },

  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const clubIdValue = this.isAdmin ? this.clubId : null;
        const coachId = this.isCoach ? this.getProfile?.id : null;
        const teamId = this.isCoach ? this.getProfile?.teamId : null;

        // Calculate date range for trends (last 7 days)
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 6);

        const response = await Nutrition.getTeamNutritionDashboard(
          clubIdValue,
          teamId,
          {
            start: startDate.toISOString().split("T")[0],
            end: endDate.toISOString().split("T")[0],
          },
          coachId
        );

        if (response.data?.success) {
          this.dashboardData = response.data.dashboard || {};
          this.buildWeekTrends(response.data.daily_trends || []);
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
      } finally {
        this.loading = false;
      }
    },

    buildWeekTrends(dailyTrends) {
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const today = new Date().toISOString().split("T")[0];
      const trends = [];

      // Build last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split("T")[0];
        const dayData = dailyTrends.find((d) => d.date === dateStr) || {};

        trends.push({
          date: dateStr,
          dayName: dayNames[date.getDay()],
          isToday: dateStr === today,
          compliance: dayData.avg_compliance || 0,
          avgHydration: dayData.avg_hydration || 0,
          mealsLogged: dayData.total_meals || 0,
        });
      }

      this.weekTrends = trends;
    },

    selectPlayer(player) {
      this.$emit("select-player", player);
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    getStatusStyle(player) {
      const compliance = player.compliance || 0;
      if (compliance >= 50) {
        return { backgroundColor: "#fff3cd", color: "#856404" };
      }
      return { backgroundColor: "#f8d7da", color: "#dc3545" };
    },
  },
};
</script>

<style scoped lang="scss">
.nutrition-dashboard {
  padding: 16px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;

  .card-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  &.total .card-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }

  &.logged .card-icon {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  }

  &.hydration .card-icon {
    background: linear-gradient(135deg, #17a2b8 0%, #007bff 100%);
  }

  .card-content {
    display: flex;
    flex-direction: column;
  }

  .card-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
    line-height: 1.2;
  }

  .card-label {
    font-size: 0.8rem;
    color: #666;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e9ecef;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 1rem;
    color: #333;
  }

  .attention-count {
    background: #dc3545;
    color: white;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 10px;
  }
}

.attention-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
}

.attention-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.attention-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e9ecef;
  }
}

.player-avatar {
  .avatar-img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 0.85rem;
  }
}

.player-info {
  flex: 1;
  min-width: 0;

  .player-name {
    display: block;
    font-weight: 600;
    color: #333;
    font-size: 0.9rem;
  }

  .attention-reason {
    font-size: 0.8rem;
    color: #666;
  }
}

.status-indicator {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.all-good {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;

  .all-good-icon {
    color: #28a745;
    margin-bottom: 12px;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
  }
}

.trends-section {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
}

.trends-chart {
  padding: 16px 0;
}

.trend-bars {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 120px;
  gap: 8px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e9ecef;
}

.trend-bar-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  position: relative;
}

.trend-bar {
  width: 100%;
  max-width: 32px;
  background: linear-gradient(180deg, #28a745 0%, #20c997 100%);
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 0.3s ease;

  &.today {
    background: linear-gradient(180deg, #007bff 0%, #17a2b8 100%);
  }
}

.trend-label {
  position: absolute;
  bottom: -20px;
  font-size: 0.7rem;
  color: #666;
  text-transform: uppercase;
}

.trend-legend {
  text-align: center;
  margin-top: 8px;

  span {
    font-size: 0.75rem;
    color: #999;
  }
}

.quick-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;

  .stat-value {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 4px;
  }

  .stat-label {
    font-size: 0.75rem;
    color: #666;
  }
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .summary-cards {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .summary-card {
    padding: 12px;

    .card-icon {
      width: 40px;
      height: 40px;
    }

    .card-value {
      font-size: 1.25rem;
    }
  }

  .quick-stats {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .trend-bars {
    height: 80px;
  }
}
</style>
